import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import TomSelect from '../../components/common/TomSelect';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SuccessModal from '../../components/common/SuccessModal';
import FailModal from '../../components/common/FailModal';
import DateRangeFilter from '../../components/common/DateRangeFilter';
import { useSelection } from '../../hooks/useSelection';
import { validateField } from '../../utils/validation';
import { usePagination } from '../../hooks/usePagination';
import { useAuth } from '../../contexts/AuthProvider';
import { GenericView, SearchInput } from '../../components/common/table';


const Standard = () => {
  // Actions dropdown refs for manual Bootstrap Dropdown initialization
  const actionsToggleRef = useRef(null);
  const actionsContainerRef = useRef(null);
  const actionsDropdownInstanceRef = useRef(null);
  const [isActionsOpen, setIsActionsOpen] = useState(false);

  useEffect(() => {
    const toggleEl = actionsToggleRef.current;
    const containerEl = actionsContainerRef.current;
    if (!toggleEl || !containerEl) return;
    if (typeof window !== 'undefined' && window.bootstrap?.Dropdown) {
      const instance = new window.bootstrap.Dropdown(toggleEl, { autoClose: true });
      actionsDropdownInstanceRef.current = instance;
      const onShow = () => setIsActionsOpen(true);
      const onHide = () => setIsActionsOpen(false);
      containerEl.addEventListener('show.bs.dropdown', onShow);
      containerEl.addEventListener('hide.bs.dropdown', onHide);
      return () => {
        containerEl.removeEventListener('show.bs.dropdown', onShow);
        containerEl.removeEventListener('hide.bs.dropdown', onHide);
        try { instance.dispose(); } catch {}
        actionsDropdownInstanceRef.current = null;
      };
    }
  }, []);

  // Permission system
  const { user, can, capabilitiesLoading } = useAuth();
  const canStandard = (key) => can(key, 'standard');

  const navigate = useNavigate();
  // Pagination
  const pagination = usePagination(10);

  const [search, setSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [appliedSearchTerm, setAppliedSearchTerm] = useState('');

  const handleSearchInputChange = (value) => {
    setSearch(value);
    setSearchTerm(value);
  };

  const handleSearchSubmit = () => {
    setAppliedSearchTerm(searchTerm);
    pagination.setPage(0);
  };

  // Selection
  const {
    selectedIds,
    setSelectedIds,
    handleCheckboxClick,
    handleSelectAll
  } = useSelection();

  // Date filter
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Data and form state
  const [standards, setStandards] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    standardType: '',
    services: [],
    prefixCertificate: '',
    accredited: '',
    standardScheme: '',
    taggingIds: []
  });
  const [editingId, setEditingId] = useState(null);
  const [errors, setErrors] = useState({});
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [certificationSchemes, setCertificationSchemes] = useState([]);
  const [services, setServices] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFailModal, setShowFailModal] = useState(false);
  const [failMessage, setFailMessage] = useState('');

  // Fetch standards
  const fetchStandards = async (searchValue = appliedSearchTerm) => {
    try {
      setIsLoading(true);
      const response = await api.get('/md/standards/page', {
        params: {
          page: pagination.page,
          size: pagination.size,
          search: searchValue,
          from: fromDate,
          to: toDate
        }
      });
      setStandards(response.data.content || []);
      pagination.setTotalPages(response.data.totalPages);
      pagination.setTotal(response.data.totalElements);
      pagination.setCurrentPageItems(response.data.content.length);
    } catch (error) {
      let errorMessage = 'Failed to fetch standards.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view standards.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching standards.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching standards.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setStandards([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Scheme Categories, services, and taggings (for standards module only)
  const fetchMeta = async () => {
    try {
      const [schemesResponse, servicesResponse, taggingResponse] = await Promise.all([
        api.get('/md/certification-scheme'),
        api.get('/md/services'),
        api.get('/md/tagging', { params: { moduleKey: 'standards' } })
      ]);
      setCertificationSchemes(schemesResponse.data || []);
      setServices(servicesResponse.data || []);
      setTaggingOptions(taggingResponse.data || []);
    } catch (error) {
      let errorMessage = 'Failed to fetch metadata.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 403:
            errorMessage = 'You do not have permission to view metadata.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 500:
            errorMessage = 'Server encountered an error while fetching metadata.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while fetching metadata.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
      setCertificationSchemes([]);
      setServices([]);
      setTaggingOptions([]);
    }
  };

  useEffect(() => {
    fetchStandards();
    fetchMeta();
  }, [pagination.page, pagination.size, fromDate, toDate, appliedSearchTerm]);

  // Open modal for create
  const handleCreate = () => {
    if (!canStandard('create')) { setFailMessage('You do not have permission to create.'); setShowFailModal(true); return; }
    setFormData({
      name: '',
      standardType: '',
      services: [],
      prefixCertificate: '',
      accredited: '',
      standardScheme: '',
      taggingIds: []
    });
    setEditingId(null);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-standard'));
    modal.show();
  };

  // Open modal for edit
  const handleEdit = (item) => {
    if (!canStandard('edit')) { setFailMessage('You do not have permission to edit.'); setShowFailModal(true); return; }
    setFormData({
      name: item.name,
      standardType: item.certificationScheme?.id?.toString() || '',
      services: item.serviceIds?.map(id => id.toString()) || [],
      prefixCertificate: item.prefixCertificate,
      accredited: item.accredited ? '1' : '2',
      standardScheme: item.standardScheme,
      taggingIds: Array.isArray(item.taggings) ? item.taggings.map(t => String(t.id)) : []
    });
    setEditingId(item.id);
    setErrors({});
    const modal = new window.bootstrap.Modal(document.getElementById('modal-standard'));
    modal.show();
  };

  // Real-time validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'name' || name === 'prefixCertificate' || name === 'standardScheme') {
      error = validateField(name, value, { required: true });
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // TomSelect change handler
  const handleSelectChange = (name, value) => {
    if (name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      setFormData((prev) => ({ ...prev, taggingIds: arr }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
    let error = null;
    if (name === 'services') {
      error = value.length === 0 ? 'At least one service must be selected' : null;
    }
    if (name === 'standardType') {
      error = !value ? 'Scheme Category is required' : null;
    }
    if (name === 'accredited') {
      error = !value ? 'Accredited status is required' : null;
    }
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Unified handleSubmit for both create and edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errorName = validateField('name', formData.name, { required: true });
    const errorPrefix = validateField('prefixCertificate', formData.prefixCertificate, { required: true });
    const errorScheme = validateField('standardScheme', formData.standardScheme, { required: true });
    const errorServices = formData.services.length === 0 ? 'At least one service must be selected' : null;
    const errorType = !formData.standardType ? 'Scheme Category is required' : null;
    const errorAccredited = !formData.accredited ? 'Accredited status is required' : null;
    const newErrors = {
      name: errorName,
      prefixCertificate: errorPrefix,
      standardScheme: errorScheme,
      services: errorServices,
      standardType: errorType,
      accredited: errorAccredited
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;
    try {
      const payload = {
        name: formData.name,
        prefixCertificate: formData.prefixCertificate,
        accredited: formData.accredited === '1',
        certificationScheme: { id: parseInt(formData.standardType) },
        serviceIds: formData.services.map(id => parseInt(id)),
        standardScheme: formData.standardScheme,
        taggings: (formData.taggingIds || []).map(id => ({ id: parseInt(id, 10) })).filter(t => !isNaN(t.id))
      };
      if (editingId) {
        await api.put(`/md/standards/${editingId}`, { id: editingId, ...payload });
        setSuccessMessage('Standard has been successfully updated.');
      } else {
        await api.post('/md/standards', payload);
        setSuccessMessage('Standard has been successfully added.');
      }
      setEditingId(null);
      setFormData({
        name: '',
        standardType: '',
        services: [],
        prefixCertificate: '',
        accredited: '',
        standardScheme: '',
        taggingIds: []
      });
      setErrors({});
      await fetchStandards();
      const modal = document.getElementById('modal-standard');
      const bootstrapModal = window.bootstrap?.Modal.getInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
        setTimeout(() => setShowSuccessModal(true), 300);
      } else {
        setShowSuccessModal(true);
      }
    } catch (error) {
      let errorMessage = 'Failed to save standard.';
      let technicalDetails = '';

      if (error.response) {
        switch (error.response.status) {
          case 400:
            errorMessage = 'Invalid standard data provided.';
            technicalDetails = error.response.data.message || 'The server rejected the request due to invalid data';
            break;
          case 403:
            errorMessage = 'You do not have permission to perform this action.';
            technicalDetails = 'Required role or permission is missing';
            break;
          case 409:
            errorMessage = 'A standard with this name already exists.';
            technicalDetails = `Duplicate entry for name: "${formData.name}"`;
            break;
          case 500:
            errorMessage = 'Server encountered an error while saving.';
            technicalDetails = error.response.data.message || 'Internal server error occurred';
            break;
          default:
            errorMessage = 'An error occurred while saving the standard.';
            technicalDetails = `Server responded with status: ${error.response.status}`;
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to the server.';
        technicalDetails = 'Network error or server is not responding';
      } else {
        errorMessage = 'An unexpected error occurred.';
        technicalDetails = error.message;
      }

      setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
      setShowFailModal(true);
    }
  };

  // Edit selected (only works for single selection)
  const editSelected = () => {
    if (selectedIds.length !== 1) {
      setFailMessage('Please select exactly one standard to edit.\n\nTechnical Details: Multiple or no items selected');
      setShowFailModal(true);
      return;
    }
    const selectedId = selectedIds[0];
    const selectedItem = standards.find(item => item.id === selectedId);
    if (selectedItem) {
      handleEdit(selectedItem);
    }
  };

  // Delete selected (bulk)
  const deleteSelected = () => {
    if (!canStandard('delete')) { setFailMessage('You do not have permission to delete.'); setShowFailModal(true); return; }
    if (selectedIds.length === 0) {
      setFailMessage('Please select at least one standard to delete.\n\nTechnical Details: No items selected for deletion');
      setShowFailModal(true);
      return;
    }
    setConfirmAction(() => async () => {
      try {
        await api.delete('/md/standards/bulk', { data: selectedIds });
        setSelectedIds([]);
        await fetchStandards();
        setShowDeleteConfirmation(false);
        setSuccessMessage('Selected standards have been successfully deleted.');
        setShowSuccessModal(true);
      } catch (error) {
        let errorMessage = 'Failed to delete standards.';
        let technicalDetails = '';

        if (error.response) {
          switch (error.response.status) {
            case 403:
              errorMessage = 'You do not have permission to delete these items.';
              technicalDetails = 'Required role or permission is missing';
              break;
            case 404:
              errorMessage = 'One or more standards not found.';
              technicalDetails = 'Some items may have been already deleted';
              break;
            case 500:
              errorMessage = 'Server error while deleting items.';
              technicalDetails = error.response.data.message || 'Internal server error during deletion';
              break;
            default:
              errorMessage = 'An error occurred while deleting the items.';
              technicalDetails = `Server responded with status: ${error.response.status}`;
          }
        } else if (error.request) {
          errorMessage = 'Unable to connect to the server.';
          technicalDetails = 'Network error or server is not responding';
        } else {
          errorMessage = 'An unexpected error occurred during deletion.';
          technicalDetails = error.message;
        }

        setShowDeleteConfirmation(false);
        setFailMessage(`${errorMessage}\n\nTechnical Details: ${technicalDetails}`);
        setShowFailModal(true);
      }
    });
    setShowDeleteConfirmation(true);
  };

  // Modal cleanup
  useEffect(() => {
    const modal = document.getElementById('modal-standard');
    modal?.addEventListener('hidden.bs.modal', () => {
      setFormData({
        name: '',
        standardType: '',
        services: [],
        prefixCertificate: '',
        accredited: '',
        standardScheme: '',
        taggingIds: []
      });
      setEditingId(null);
    });
    return () => {
      modal?.removeEventListener('hidden.bs.modal', () => { });
    };
  }, []);

  const columns = [
    { key: 'name', label: 'Name', sortable: true },
    { key: 'certificationScheme', label: 'Scheme Category', sortable: true, render: (value) => value?.name || '-' },
    { key: 'prefixCertificate', label: 'Prefix Certificate', sortable: true },
    {
      key: 'serviceIds', label: 'Services', sortable: false, render: (value) => {
        if (!value || !Array.isArray(value) || value.length === 0) return '-';
        return (
          <div className="badges-list">
            {value.map((serviceId, index) => {
              const service = services.find(s => s.id === serviceId);
              return (
                <span key={index} className="badge">
                  {service?.name || `Service ${serviceId}`}
                </span>
              );
            })}
          </div>
        );
      }
    },
    { key: 'accredited', label: 'Accredited', sortable: true, render: (value) => value ? 'Yes' : 'No' },
    { key: 'standardScheme', label: 'Standard Scheme', sortable: true },
    {
      key: 'taggings', label: 'Tagging', sortable: false, render: (value) => {
        if (!Array.isArray(value) || value.length === 0) return '—';
        return (
          <div className="d-flex flex-wrap gap-1">
            {value.map((t) => (
              <span key={t.id} className="badge bg-blue-lt text-blue">{t.name}</span>
            ))}
          </div>
        );
      }
    },
  ];

  return (
    <>
      {/* Page header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <h2 className="page-title">Standard</h2>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <a href="#" className="btn btn-primary d-none d-sm-inline-block"
                  onClick={handleCreate}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon" width="24" height="24" viewBox="0 0 24 24"
                    strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Create
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page body */}
      <div className="page-body">
        <div className="container-xl">
          <div className="card">
            <div className="card-body">
              <GenericView
                data={standards}
                columns={columns}
                isLoading={isLoading || capabilitiesLoading}
                emptyStateMessage="No standards found"
                checkboxSelection={true}
                selectedRows={selectedIds}
                onSelectRow={(itemId, isSelected) => {
                  if (isSelected) {
                    setSelectedIds([...selectedIds, itemId]);
                  } else {
                    setSelectedIds(selectedIds.filter(id => id !== itemId));
                  }
                }}
                onSelectAllRows={(itemIds) => {
                  if (Array.isArray(itemIds) && itemIds.length > 0) {
                    setSelectedIds([...new Set([...selectedIds, ...itemIds])]);
                  } else {
                    setSelectedIds([]);
                  }
                }}
                onEdit={canStandard('edit') ? (item) => {
                  setSelectedIds([item.id]);
                  handleEdit(item);
                } : undefined}
                onDelete={canStandard('delete') ? (item) => {
                  setSelectedIds([item.id]);
                  deleteSelected();
                } : undefined}
                searchComponent={
                  <div className="d-flex gap-2">
                    <SearchInput
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleSearchSubmit();
                        }
                      }}
                      placeholder="Search..."
                      uniqueId="standard-search"
                    />
                    <button type="button" className="btn btn-primary" onClick={handleSearchSubmit}>
                      Search
                    </button>
                  </div>
                }
                actionButtons={
                  <div className="d-flex gap-2">
                    {(canStandard('edit') || canStandard('delete')) && (
                      <div ref={actionsContainerRef} className="dropdown">
                        <a
                          ref={actionsToggleRef}
                          href="#"
                          className="btn dropdown-toggle"
                          data-bs-toggle="dropdown"
                          aria-expanded={isActionsOpen}
                          onClick={(e) => {
                            e.preventDefault();
                            const inst = actionsDropdownInstanceRef.current;
                            if (inst?.toggle) inst.toggle();
                            else setIsActionsOpen(prev => !prev);
                          }}
                        >Actions</a>
                        <div className="dropdown-menu">
                          {canStandard('edit') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); editSelected(); }}
                              style={{ color: selectedIds.length === 1 ? "" : "#ccc", pointerEvents: selectedIds.length === 1 ? "auto" : "none" }}>
                              Edit
                            </a>
                          )}
                          {canStandard('delete') && (
                            <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); deleteSelected(); }}
                              style={{ color: selectedIds.length ? "" : "#ccc", pointerEvents: selectedIds.length ? "auto" : "none" }}>
                              Delete
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                }
                pagination={{
                  currentPage: pagination.page + 1,
                  totalRows: pagination.total,
                  rowsPerPage: pagination.size,
                  onPageChange: (page) => pagination.setPage(page - 1),
                  onRowsPerPageChange: (size) => {
                    pagination.setSize(size);
                    pagination.setPage(0);
                  }
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                uniqueId="standard-table"
              />
            </div>
          </div>
        </div>
      </div>
      {/* Modal for add/edit standard */}
      <div className="modal modal-blur fade" id="modal-standard" tabIndex="-1" role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{editingId ? 'Edit Standard' : 'Add New Standard'}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label required">Name</label>
                  <input
                    type="text"
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter name"
                  />
                  {errors.name && (
                    <div className="invalid-feedback">{errors.name}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label required">Prefix Certificate</label>
                  <input
                    type="text"
                    className={`form-control ${errors.prefixCertificate ? 'is-invalid' : ''}`}
                    name="prefixCertificate"
                    value={formData.prefixCertificate}
                    onChange={handleInputChange}
                    placeholder="Enter prefix certificate"
                  />
                  {errors.prefixCertificate && (
                    <div className="invalid-feedback">{errors.prefixCertificate}</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label required">Accredited</label>
                  <TomSelect
                    name="accredited"
                    value={formData.accredited}
                    onChange={handleSelectChange}
                    options={[
                      { id: '1', name: 'Yes' },
                      { id: '2', name: 'No' }
                    ]}
                    placeholder="Select accredited status"
                    required={true}
                    error={errors.accredited}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={false}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label required">Scheme Category</label>
                  <TomSelect
                    name="standardType"
                    value={formData.standardType}
                    onChange={handleSelectChange}
                    options={certificationSchemes.map(scheme => ({ id: scheme.id.toString(), name: scheme.name }))}
                    placeholder="Select Scheme Category"
                    required={true}
                    error={errors.standardType}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={false}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label required">Services</label>
                  <TomSelect
                    name="services"
                    value={formData.services}
                    onChange={handleSelectChange}
                    options={services.map(service => ({ id: service.id.toString(), name: service.name }))}
                    placeholder="Select services"
                    required={true}
                    error={errors.services}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={true}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label required">Standard Scheme</label>
                  <input
                    type="text"
                    className={`form-control ${errors.standardScheme ? 'is-invalid' : ''}`}
                    name="standardScheme"
                    value={formData.standardScheme}
                    onChange={handleInputChange}
                    placeholder="Enter standard scheme"
                  />
                  {errors.standardScheme && (
                    <div className="invalid-feedback">{errors.standardScheme}</div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Tagging</label>
                  <TomSelect
                    name="taggingIds"
                    value={formData.taggingIds || []}
                    onChange={handleSelectChange}
                    options={taggingOptions.map(t => ({ id: t.id.toString(), name: t.name }))}
                    placeholder="Select tagging(s)"
                    required={false}
                    error={errors.taggingIds}
                    valueField="id"
                    labelField="name"
                    searchField="name"
                    multiple={true}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <a href="#" className="btn btn-link link-secondary btn-3" data-bs-dismiss="modal">Cancel</a>
                <button type="submit" className="btn btn-primary ms-auto" id="createBtn">
                  {editingId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmation}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmAction || (() => { })}
        title="Delete Confirmation"
        message={selectedIds.length > 0
          ? "Are you sure you want to delete the selected standards? This action cannot be undone."
          : "Are you sure you want to delete this standard? This action cannot be undone."}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title="Success"
        message={successMessage}
        buttonText="OK"
      />
      {/* Add FailModal */}
      <FailModal
        isOpen={showFailModal}
        onClose={() => setShowFailModal(false)}
        title="Operation Failed"
        message={failMessage}
        buttonText="Close"
      />
    </>
  );
};

export default Standard;
